import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Empty } from 'antd'
import { LeftOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAuthSession } from '@/lib/session'
import { useToast } from '@/contexts/ToastContext'
import { ADVISORY_ACCESS_DENIED_MESSAGE, isAdvisoryAccessDenied } from '@/features/advisory-ai/access'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { StatsGrid } from '@/features/advisory-ai/calls/components'
import {
  useConsolidatedAnalyses,
  useCreateConsolidatedAnalysisMutation,
  useDeleteConsolidatedAnalysisMutation,
  type EnrichedConsolidatedAnalysis,
} from '@/features/advisory-ai/hooks'
import { GROUP_STATUS_META } from '@/features/advisory-ai/consolidated/utils'
import type { CallStatus } from '@/features/advisory-ai/calls/utils'
import { ConsolidatedCard } from './components/ConsolidatedCard'
import { CreateConsolidatedModal, type SelectedCompany } from './components/CreateConsolidatedModal'

const SECTION_ORDER: CallStatus[] = ['needsAttention', 'inProgress', 'generating', 'ready']

export default function AdvisoryConsolidatedList() {
  const navigate = useNavigate()
  const clientId = useAuthSession()?.currentClientId || ''
  const { addToast } = useToast()

  const { groups, isLoading, isFetching, error, refetch } = useConsolidatedAnalyses(clientId)
  const createMutation = useCreateConsolidatedAnalysisMutation()
  const deleteMutation = useDeleteConsolidatedAnalysisMutation(clientId)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const byStatus = useMemo(() => {
    const map: Record<CallStatus, EnrichedConsolidatedAnalysis[]> = {
      needsAttention: [],
      inProgress: [],
      generating: [],
      ready: [],
    }
    groups.forEach((item) => map[item.status].push(item))
    return map
  }, [groups])

  const thisWeekCount = useMemo(() => {
    const now = dayjs()
    const weekEnd = now.add(7, 'day')
    return groups.filter((item) => {
      const date = item.report?.meetingDate
      if (!date) return false
      const d = dayjs(date)
      return d.isValid() && d.isAfter(now) && d.isBefore(weekEnd)
    }).length
  }, [groups])

  const notPermitted = () => addToast(ADVISORY_ACCESS_DENIED_MESSAGE, 'warning')

  const handleCreate = async (title: string, companies: SelectedCompany[]) => {
    if (!clientId) {
      addToast('No active client selected', 'warning')
      return
    }
    try {
      const group = await createMutation.mutateAsync({ clientId, title, companies })
      setIsModalOpen(false)
      navigate(`/adviceprep/consolidated/${group.advisoryCallId}`)
    } catch (err) {
      if (isAdvisoryAccessDenied(err)) {
        notPermitted()
        return
      }
      addToast(err instanceof Error ? err.message : 'Failed to create consolidated analysis', 'error')
    }
  }

  const handleDelete = async (advisoryCallId: string) => {
    const item = groups.find((g) => g.group.advisoryCallId === advisoryCallId)
    if (!item) return
    try {
      await deleteMutation.mutateAsync(item.group)
      addToast('Consolidated analysis deleted', 'success')
    } catch (err) {
      if (isAdvisoryAccessDenied(err)) {
        notPermitted()
        return
      }
      addToast(err instanceof Error ? err.message : 'Failed to delete', 'error')
    }
  }

  if (!clientId) {
    return <Alert type="warning" showIcon message="No active client selected" />
  }

  return (
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="feature-shell rounded-2xl">
      <span className="feature-shell__glow" aria-hidden />
      <div className="feature-shell__header px-6 pt-5 pb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button icon={<LeftOutlined />} onClick={() => navigate('/adviceprep')}>
              Back
            </Button>
            <h1 className="font-display text-2xl font-bold text-gray-900">Consolidated Analyses</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              className="feature-button-solid"
              icon={<PlusOutlined />}
              loading={createMutation.isPending}
              onClick={() => setIsModalOpen(true)}
            >
              Add consolidated analysis
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="p-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load consolidated analyses"
            description={error instanceof Error ? error.message : 'Unknown error'}
          />
        </div>
      ) : null}

      <div className="p-6">
        <div className="mb-6">
          <StatsGrid
            thisWeekCalls={thisWeekCount}
            needsPrepCount={byStatus.needsAttention.length}
            inProgressCount={byStatus.inProgress.length + byStatus.generating.length}
            readyCount={byStatus.ready.length}
          />
        </div>

        {/* data-tour anchors the assistant walkthroughs — see assistant/mockData.ts */}
        <div data-tour="group-list">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading consolidated analyses...</div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Empty description="No consolidated analyses yet" />
          </div>
        ) : (
          SECTION_ORDER.map((status) => {
            const items = byStatus[status]
            if (items.length === 0) return null
            const meta = GROUP_STATUS_META[status]
            return (
              <div key={status} className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-3 h-3 rounded-full ${meta.dotColor}`} />
                  <h2 className="text-lg font-semibold text-gray-900">
                    {meta.title} {items.length}
                  </h2>
                </div>
                {items.map((item) => (
                  <ConsolidatedCard
                    key={item.group.advisoryCallId}
                    item={item}
                    isDeleting={deleteMutation.isPending}
                    onOpen={(id) => navigate(`/adviceprep/consolidated/${id}`)}
                    onPresent={(id) => navigate(`/adviceprep/consolidated/${id}?step=4`)}
                    onDelete={handleDelete}
                    onBlocked={notPermitted}
                  />
                ))}
              </div>
            )
          })
        )}
        </div>
      </div>

      <CreateConsolidatedModal
        open={isModalOpen}
        clientId={clientId}
        isCreating={createMutation.isPending}
        onCancel={() => setIsModalOpen(false)}
        onCreate={handleCreate}
      />
    </FeatureThemeProvider>
  )
}
